import type { ChangeEventHandler, CSSProperties, RefObject } from "react";

type BackgroundToolbarProps = {
  containerStyle?: CSSProperties;
  onActivate?: () => void;
  isOpen: boolean;
  toolbarRef: RefObject<HTMLDivElement | null>;
  inputRef: RefObject<HTMLInputElement | null>;
  onUpload: ChangeEventHandler<HTMLInputElement>;
  onCollapse: () => void;
  onExpand: () => void;
  onGoogleMap: () => void;
  onOpenFile: () => void;
  status: string | null;
  transparency: number;
  onTransparencyChange: (value: number) => void;
  hasBackgroundAsset: boolean;
  isBackgroundLoading: boolean;
  isReferenceMode: boolean;
  hasReference: boolean;
  onToggleReference: () => void;
  onAlignHorizontal: () => void;
  onAlignVertical: () => void;
  alignmentAngleDraft: string;
  onAlignmentAngleDraftChange: (value: string) => void;
  isCropMode: boolean;
  onToggleCrop: () => void;
  onClearBackground: () => void;
};

export function BackgroundToolbar({
  containerStyle,
  onActivate,
  isOpen,
  toolbarRef,
  inputRef,
  onUpload,
  onCollapse,
  onExpand,
  onGoogleMap,
  onOpenFile,
  status,
  transparency,
  onTransparencyChange,
  hasBackgroundAsset,
  isBackgroundLoading,
  isReferenceMode,
  hasReference,
  onToggleReference,
  onAlignHorizontal,
  onAlignVertical,
  alignmentAngleDraft,
  onAlignmentAngleDraftChange,
  isCropMode,
  onToggleCrop,
  onClearBackground,
}: BackgroundToolbarProps) {
  const lightIconButtonClass =
    "inline-flex h-10 w-10 items-center justify-center rounded-md border border-[rgba(74,58,31,0.12)] bg-white text-[#1f2937] transition active:scale-110";
  const activeIconButtonClass =
    "border border-[rgba(29,92,171,0.24)] bg-[rgba(29,92,171,0.08)] text-[#1d5cab]";

  return (
    <div
      className="absolute left-1/2 top-7 z-30 -translate-x-1/2"
      style={containerStyle}
      onMouseDownCapture={onActivate}
      onFocusCapture={onActivate}
    >
      {isOpen ? (
        <div
          ref={toolbarRef}
          className="flex items-center gap-2 rounded-[6px] border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.96)] px-3 py-2 shadow-[0_20px_40px_rgba(58,45,20,0.14)] backdrop-blur-[10px]"
        >
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,image/*"
            className="hidden"
            style={{ display: "none" }}
            tabIndex={-1}
            onChange={onUpload}
          />
          <button
            type="button"
            className={lightIconButtonClass}
            title="Minimize background toolbar"
            aria-label="Minimize background toolbar"
            onClick={onCollapse}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none">
              <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <button
            type="button"
            className={lightIconButtonClass}
            title="Carica da Google Map"
            aria-label="Carica da Google Map"
            onClick={onGoogleMap}
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              aria-hidden="true"
              className="h-6 w-6 shrink-0"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <clipPath id="mapclip48">
                  <rect x="14" y="4" width="30" height="30" rx="4" />
                </clipPath>
              </defs>
              <rect x="14" y="4" width="30" height="30" rx="4" fill="#e8f0fe" stroke="#4285f4" strokeWidth="1.5" />
              <g clipPath="url(#mapclip48)">
                <line x1="14" y1="16" x2="44" y2="16" stroke="#c5cae9" strokeWidth="2" />
                <line x1="14" y1="24" x2="44" y2="24" stroke="#c5cae9" strokeWidth="2" />
                <line x1="26" y1="4" x2="26" y2="34" stroke="#c5cae9" strokeWidth="2" />
                <line x1="36" y1="4" x2="36" y2="34" stroke="#c5cae9" strokeWidth="2" />
                <rect x="27" y="17" width="8" height="6" rx="1" fill="#a8d5a2" />
                <circle cx="31" cy="13" r="3" fill="#ea4335" />
                <circle cx="31" cy="13" r="1.2" fill="white" />
                <line x1="31" y1="16" x2="31" y2="19" stroke="#ea4335" strokeWidth="1.2" strokeLinecap="round" />
              </g>
              <line x1="4" y1="19" x2="12" y2="19" stroke="#4285f4" strokeWidth="1.5" strokeLinecap="round" />
              <polyline
                points="10,16 13,19 10,22"
                fill="none"
                stroke="#4285f4"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line x1="16" y1="30" x2="40" y2="30" stroke="#ea4335" strokeWidth="2" strokeLinecap="round" />
              <g transform="translate(38, 26) rotate(45)">
                <rect x="0" y="0" width="4" height="9" rx="1" fill="#fbbc04" stroke="#e0a800" strokeWidth="0.5" />
                <polygon
                  points="0,9 4,9 2,13"
                  fill="#f5e6c8"
                  stroke="#e0a800"
                  strokeWidth="0.5"
                  strokeLinejoin="round"
                />
              </g>
            </svg>
          </button>
          <button
            type="button"
            className="inline-flex h-10 items-center justify-center rounded-md border border-[rgba(74,58,31,0.12)] bg-white px-4 text-[11px] font-medium text-[#1f2937] transition active:scale-110"
            onClick={onOpenFile}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="mr-1.5 h-4 w-4 fill-none">
              <path
                d="M12 16V5m0 0 4 4m-4-4L8 9M5 18v1h14v-1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Background
          </button>

          {status && <span className="sr-only">{status}</span>}

          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#4b3e27]">Transparancy</span>
            <input
              type="range"
              min="0"
              max="100"
              value={transparency}
              onChange={(event) => onTransparencyChange(Number(event.target.value))}
              className="w-24 accent-[#1d5cab]"
            />
            <span className="w-10 text-right text-[11px] font-medium text-[#1f2937]">{transparency}%</span>
          </div>

          <button
            type="button"
            disabled={!hasBackgroundAsset || isBackgroundLoading}
            className={`inline-flex h-10 w-10 items-center justify-center rounded-md transition active:scale-110 disabled:cursor-not-allowed disabled:opacity-40 ${
              isReferenceMode ? activeIconButtonClass : lightIconButtonClass
            }`}
            title="Reference line"
            aria-label="Reference line"
            onClick={onToggleReference}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none">
              <path
                d="M4 18 20 6M4 18h5M20 6h-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {hasReference && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={lightIconButtonClass}
                title="Allinea orizzontalmente"
                aria-label="Allinea orizzontalmente"
                onClick={onAlignHorizontal}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none">
                  <path
                    d="M4 12h16M8 8l-4 4 4 4M16 8l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                className={lightIconButtonClass}
                title="Allinea verticalmente"
                aria-label="Allinea verticalmente"
                onClick={onAlignVertical}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none">
                  <path
                    d="M12 4v16M8 8l4-4 4 4M8 16l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <span className="text-[11px] font-medium text-[#4b3e27]">°</span>
              <input
                className="h-10 w-20 rounded-md border border-[rgba(74,58,31,0.16)] bg-white px-3 text-[12px] font-medium text-[#1f2937] outline-none focus:border-[#1d5cab]"
                value={alignmentAngleDraft}
                onChange={(event) => onAlignmentAngleDraftChange(event.target.value)}
              />
            </div>
          )}

          <button
            type="button"
            disabled={!hasBackgroundAsset || isBackgroundLoading}
            className={`inline-flex h-10 w-10 items-center justify-center rounded-md border transition active:scale-110 disabled:cursor-not-allowed disabled:opacity-40 ${
              isCropMode ? activeIconButtonClass : "border-[rgba(74,58,31,0.12)] bg-white text-[#1f2937]"
            }`}
            title="Background cropping mode"
            aria-label="Background cropping mode"
            onClick={onToggleCrop}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none">
              <path
                d="M7 3v11a3 3 0 0 0 3 3h11M3 7h11a3 3 0 0 1 3 3v11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {hasBackgroundAsset && (
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[rgba(74,58,31,0.12)] bg-white text-[#991b1b] transition active:scale-110"
              title="Remove background"
              aria-label="Remove background"
              onClick={onClearBackground}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                <path d="M4 7h16" strokeWidth="2" strokeLinecap="round" />
                <path d="M9 7V5h6v2M7 7l1 12h8l1-12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10 11v5M14 11v5" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
      ) : (
        <div className="flex justify-center">
          <button
            type="button"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-[6px] border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.96)] px-5 py-2 text-[11px] font-medium text-[#1F2937] shadow-[0_20px_40px_rgba(58,45,20,0.14)] backdrop-blur-[10px] transition active:scale-110"
            onClick={onExpand}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none">
              <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Background
          </button>
        </div>
      )}
    </div>
  );
}
